/**
 * Integrations Settings Page
 *
 * Allows users to configure project integrations (JIRA, GitHub, Jenkins, Slack, MS Teams)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  PlayArrow as TestIcon,
  BugReport as JiraIcon,
  GitHub as GitHubIcon,
  Build as JenkinsIcon,
  Chat as SlackIcon,
  Groups as TeamsIcon,
} from '@mui/icons-material';
import {
  Project,
  Integration,
  IntegrationType,
  IntegrationCreate,
  IntegrationUpdate,
  IntegrationTypeInfo,
  IntegrationTestResult,
  listProjectIntegrations,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  testIntegration,
  getIntegrationTypes,
  INTEGRATION_METADATA,
} from '../../services/project.service';

interface IntegrationsPageProps {
  project: Project;
  onBack: () => void;
}

// Icon mapping for integration types
const getIntegrationIcon = (type: IntegrationType) => {
  const iconMap: Record<IntegrationType, React.ReactNode> = {
    jira: <JiraIcon />,
    github: <GitHubIcon />,
    jenkins: <JenkinsIcon />,
    slack: <SlackIcon />,
    microsoft_teams: <TeamsIcon />,
  };
  return iconMap[type];
};

const IntegrationsPage: React.FC<IntegrationsPageProps> = ({ project, onBack }) => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [integrationTypes, setIntegrationTypes] = useState<IntegrationTypeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedType, setSelectedType] = useState<IntegrationType | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    config: Record<string, string>;
    credentials: Record<string, string>;
  }>({
    name: '',
    config: {},
    credentials: {},
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Test state
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<IntegrationTestResult | null>(null);

  useEffect(() => {
    loadData();
  }, [project.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [integrationsResponse, typesResponse] = await Promise.all([
        listProjectIntegrations(project.id, false),
        getIntegrationTypes(),
      ]);
      setIntegrations(integrationsResponse.integrations);
      setIntegrationTypes(typesResponse);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIntegration = (type: IntegrationType) => {
    setDialogMode('create');
    setSelectedType(type);
    setSelectedIntegration(null);
    setFormData({
      name: INTEGRATION_METADATA[type].displayName,
      config: {},
      credentials: {},
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleEditIntegration = (integration: Integration) => {
    setDialogMode('edit');
    setSelectedType(integration.integration_type);
    setSelectedIntegration(integration);
    setFormData({
      name: integration.name,
      config: integration.config as Record<string, string>,
      credentials: {}, // Don't load existing credentials
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedType(null);
    setSelectedIntegration(null);
  };

  const handleSave = async () => {
    if (!selectedType) return;

    try {
      setSaving(true);
      setFormError(null);

      if (dialogMode === 'create') {
        const data: IntegrationCreate = {
          integration_type: selectedType,
          name: formData.name,
          config: formData.config,
          credentials: formData.credentials,
        };
        const created = await createIntegration(project.id, data);
        setIntegrations([...integrations, created]);
      } else if (selectedIntegration) {
        const data: IntegrationUpdate = {
          name: formData.name,
          config: formData.config,
        };
        // Only include credentials if they were updated
        if (Object.keys(formData.credentials).some(k => formData.credentials[k])) {
          data.credentials = formData.credentials;
        }
        const updated = await updateIntegration(selectedIntegration.id, data);
        setIntegrations(integrations.map(i => i.id === updated.id ? updated : i));
      }

      handleCloseDialog();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (integrationId: string) => {
    if (!confirm('Are you sure you want to delete this integration?')) return;

    try {
      await deleteIntegration(integrationId);
      setIntegrations(integrations.filter(i => i.id !== integrationId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete integration');
    }
  };

  const handleTest = async (integrationId: string) => {
    try {
      setTesting(integrationId);
      setTestResult(null);
      const result = await testIntegration(integrationId);
      setTestResult(result);
      // Reload to get updated verification status
      loadData();
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Test failed',
      });
    } finally {
      setTesting(null);
    }
  };

  const handleToggleActive = async (integration: Integration) => {
    try {
      const updated = await updateIntegration(integration.id, {
        is_active: !integration.is_active,
      });
      setIntegrations(integrations.map(i => i.id === updated.id ? updated : i));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update integration');
    }
  };

  const getConfigFields = (type: IntegrationType): { key: string; label: string; required: boolean }[] => {
    const fields: Record<IntegrationType, { key: string; label: string; required: boolean }[]> = {
      jira: [
        { key: 'base_url', label: 'JIRA URL', required: true },
        { key: 'project_key', label: 'Project Key', required: true },
      ],
      github: [
        { key: 'owner', label: 'Repository Owner', required: true },
        { key: 'repo', label: 'Repository Name', required: true },
        { key: 'base_url', label: 'API URL (for Enterprise)', required: false },
      ],
      jenkins: [
        { key: 'base_url', label: 'Jenkins URL', required: true },
        { key: 'job_name', label: 'Default Job Name', required: false },
      ],
      slack: [
        { key: 'workspace', label: 'Workspace Name', required: true },
        { key: 'default_channel', label: 'Default Channel', required: true },
      ],
      microsoft_teams: [
        { key: 'tenant_id', label: 'Tenant ID', required: true },
        { key: 'team_id', label: 'Team ID', required: true },
        { key: 'channel_id', label: 'Channel ID', required: true },
      ],
    };
    return fields[type] || [];
  };

  const getCredentialFields = (type: IntegrationType): { key: string; label: string; required: boolean }[] => {
    const fields: Record<IntegrationType, { key: string; label: string; required: boolean }[]> = {
      jira: [
        { key: 'username', label: 'Username/Email', required: true },
        { key: 'api_token', label: 'API Token', required: true },
      ],
      github: [
        { key: 'token', label: 'Personal Access Token', required: true },
      ],
      jenkins: [
        { key: 'username', label: 'Username', required: true },
        { key: 'api_token', label: 'API Token', required: true },
      ],
      slack: [
        { key: 'bot_token', label: 'Bot Token', required: true },
        { key: 'signing_secret', label: 'Signing Secret', required: false },
      ],
      microsoft_teams: [
        { key: 'client_id', label: 'Client ID', required: true },
        { key: 'client_secret', label: 'Client Secret', required: true },
      ],
    };
    return fields[type] || [];
  };

  const getAvailableTypes = (): IntegrationType[] => {
    const configuredTypes = new Set(integrations.map(i => i.integration_type));
    return (['jira', 'github', 'jenkins', 'slack', 'microsoft_teams'] as IntegrationType[])
      .filter(t => !configuredTypes.has(t));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" gutterBottom>
            Integration Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure integrations for project: <strong>{project.name}</strong> ({project.key})
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => setTestResult(null)}
        >
          {testResult.message}
        </Alert>
      )}

      {/* Configured Integrations */}
      <Typography variant="h6" gutterBottom>
        Configured Integrations
      </Typography>

      {integrations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 4 }}>
          <Typography color="text.secondary">
            No integrations configured yet. Add your first integration below.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {integrations.map((integration) => (
            <Grid item xs={12} md={6} key={integration.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        mr: 2,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: INTEGRATION_METADATA[integration.integration_type].color + '20',
                        color: INTEGRATION_METADATA[integration.integration_type].color,
                      }}
                    >
                      {getIntegrationIcon(integration.integration_type)}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{integration.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {INTEGRATION_METADATA[integration.integration_type].displayName}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={integration.is_active}
                          onChange={() => handleToggleActive(integration)}
                          size="small"
                        />
                      }
                      label=""
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={integration.is_verified ? 'Verified' : 'Not Verified'}
                      color={integration.is_verified ? 'success' : 'default'}
                      icon={integration.is_verified ? <CheckIcon /> : <CloseIcon />}
                    />
                    <Chip
                      size="small"
                      label={integration.is_active ? 'Active' : 'Inactive'}
                      variant="outlined"
                    />
                  </Box>

                  {integration.verification_error && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {integration.verification_error}
                    </Alert>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={testing === integration.id ? <CircularProgress size={16} /> : <TestIcon />}
                    onClick={() => handleTest(integration.id)}
                    disabled={testing === integration.id}
                  >
                    Test
                  </Button>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditIntegration(integration)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    onClick={() => handleDelete(integration.id)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Available Integrations to Add */}
      <Typography variant="h6" gutterBottom>
        Add Integration
      </Typography>

      <Grid container spacing={2}>
        {getAvailableTypes().map((type) => (
          <Grid item xs={6} sm={4} md={2} key={type}>
            <Card
              variant="outlined"
              sx={{
                cursor: 'pointer',
                textAlign: 'center',
                p: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 1,
                },
              }}
              onClick={() => handleAddIntegration(type)}
            >
              <Box
                sx={{
                  mb: 1,
                  color: INTEGRATION_METADATA[type].color,
                }}
              >
                {getIntegrationIcon(type)}
              </Box>
              <Typography variant="body2">
                {INTEGRATION_METADATA[type].displayName}
              </Typography>
              <AddIcon fontSize="small" color="primary" sx={{ mt: 1 }} />
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Integration Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add' : 'Edit'} {selectedType && INTEGRATION_METADATA[selectedType].displayName} Integration
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Integration Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />

          <Divider sx={{ my: 2 }}>Configuration</Divider>

          {selectedType && getConfigFields(selectedType).map((field) => (
            <TextField
              key={field.key}
              fullWidth
              label={field.label}
              value={formData.config[field.key] || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, [field.key]: e.target.value },
              })}
              margin="dense"
              required={field.required}
            />
          ))}

          <Divider sx={{ my: 2 }}>Credentials</Divider>

          {selectedType && getCredentialFields(selectedType).map((field) => (
            <TextField
              key={field.key}
              fullWidth
              label={field.label}
              value={formData.credentials[field.key] || ''}
              onChange={(e) => setFormData({
                ...formData,
                credentials: { ...formData.credentials, [field.key]: e.target.value },
              })}
              margin="dense"
              required={dialogMode === 'create' && field.required}
              type="password"
              placeholder={dialogMode === 'edit' ? 'Leave empty to keep existing' : undefined}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : (dialogMode === 'create' ? 'Add Integration' : 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default IntegrationsPage;
